// Implements: F-USG-006
/**
 * `GET /api/mockups/{id}/cost-split` (owner 2026-10-19): the caller's share of one mockup's cost,
 * for Mi consumo's per-version breakdown ("Mi consumo" beside "Consumo total").
 *
 * The breakdown sends the runs it shows (its versions' and its failed turns', at most 18) and the
 * server answers which are the caller's own, plus the mockup's totals. The server takes 0..60 ids;
 * more is a caller's bug, refused here before any request. Whose share it is comes from the
 * session, never from the request. A read is a `GET`: the client's retry of a 5xx applies.
 */

import { ApiError, UNKNOWN_ERROR_CODE } from "../../../shared/api/ApiError";
import { API_BASE } from "../../../shared/api/apiBase";
import { httpRequest } from "../../../shared/api/httpClient";
import type { CostSplitView } from "../../../types/costs";

/** The most generation ids one call may carry (the server's bound). */
export const COST_SPLIT_MAX_IDS = 60;

export function costSplitPath(mockupId: string, generationIds: readonly string[]): string {
  const query = new URLSearchParams(generationIds.map((id) => ["generation_id", id]));
  return `${API_BASE}/mockups/${encodeURIComponent(mockupId)}/cost-split?${query.toString()}`;
}

/** The caller's share of `mockupId`'s cost, each asked id once, in the order given. */
export async function fetchCostSplit(
  mockupId: string,
  generationIds: readonly string[],
  signal?: AbortSignal,
): Promise<CostSplitView> {
  const ids = [...new Set(generationIds)];
  if (ids.length > COST_SPLIT_MAX_IDS) {
    throw new RangeError(`cost-split takes at most ${String(COST_SPLIT_MAX_IDS)} ids, got ${String(ids.length)}`);
  }
  const view = await httpRequest<CostSplitView>(costSplitPath(mockupId, ids), { signal });
  if (view === undefined) {
    throw new ApiError(200, UNKNOWN_ERROR_CODE, "GET /cost-split answered with no body", new Headers());
  }
  return view;
}
